import React, { useState, useEffect } from "react";
import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";

import { Select, Input } from "../../components/ui";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";

// Icons
const SettingsIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

const CopyIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
    />
  </svg>
);

const Settings = () => {
  const [settings, setSettings] = useState([]);

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const { data } = await api.get("/settings");
        setSettings(data);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, []);

  useEffect(() => {
    const socket = openSocket();

    socket.on("settings", (data) => {
      if (data.action === "update") {
        setSettings((prevState) => {
          const aux = [...prevState];
          const settingIndex = aux.findIndex((s) => s.key === data.setting.key);
          aux[settingIndex].value = data.setting.value;
          return aux;
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleChangeSetting = async (e) => {
    const selectedValue = e.target.value;
    const settingKey = e.target.name;

    try {
      await api.put(`/settings/${settingKey}`, {
        value: selectedValue,
      });
      toast.success(i18n.t("settings.success"));
    } catch (err) {
      toastError(err);
    }
  };

  const getSettingValue = (key) => {
    const setting = settings.find((s) => s.key === key);
    return setting?.value || "";
  };

  const handleCopyToken = () => {
    const token = getSettingValue("userApiToken");
    if (token) {
      navigator.clipboard.writeText(token);
      toast.success("Token copiado para a área de transferência");
    }
  };

  const userCreationOptions = [
    { value: "enabled", label: i18n.t("settings.settings.userCreation.options.enabled") },
    { value: "disabled", label: i18n.t("settings.settings.userCreation.options.disabled") },
  ];

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("settings.title")}</Title>
      </MainHeader>

      <div className="max-w-2xl space-y-4">
        {/* User Creation Setting */}
        <div className="bg-white dark:bg-dark-800 rounded-xl border border-dark-200 dark:border-dark-700 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <SettingsIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-dark-900 dark:text-white">
                {i18n.t("settings.settings.userCreation.name")}
              </h3>
              <p className="text-xs text-dark-500 dark:text-dark-400">
                Controla se novos usuários podem ser criados no sistema
              </p>
            </div>
          </div>
          <Select
            name="userCreation"
            value={settings.length > 0 ? getSettingValue("userCreation") : ""}
            onChange={handleChangeSetting}
            options={userCreationOptions}
            className="max-w-xs"
          />
        </div>

        {/* API Token Setting */}
        <div className="bg-white dark:bg-dark-800 rounded-xl border border-dark-200 dark:border-dark-700 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-warning-100 dark:bg-warning-900/30 rounded-lg">
              <svg className="w-5 h-5 text-warning-600 dark:text-warning-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-dark-900 dark:text-white">
                Token API
              </h3>
              <p className="text-xs text-dark-500 dark:text-dark-400">
                Use este token para integrar com a API externa
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Input
              readOnly
              value={settings.length > 0 ? getSettingValue("userApiToken") : ""}
              className="flex-1 font-mono text-sm"
              inputClassName="bg-dark-50 dark:bg-dark-900"
            />
            <button
              type="button"
              onClick={handleCopyToken}
              className="px-4 py-2 bg-dark-100 hover:bg-dark-200 dark:bg-dark-700 dark:hover:bg-dark-600 text-dark-700 dark:text-dark-300 rounded-lg transition-colors flex items-center gap-2"
            >
              <CopyIcon className="w-4 h-4" />
              <span className="text-sm font-medium">Copiar</span>
            </button>
          </div>
        </div>
      </div>
    </MainContainer>
  );
};

export default Settings;
